import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, scheduleTokenRefresh, clearRefreshTimer } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      scheduleTokenRefresh();
    } catch (e) {
      // A 401 here is the normal "not signed in" case and is not worth
      // shouting about. Anything else (network failure, 5xx) is a real problem
      // that would otherwise present identically as "logged out".
      if (e?.response?.status !== 401) {
        console.error("Session probe failed:", e);
      }
      setUser(false);
      clearRefreshTimer();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Always ask the server. The session now lives in an HttpOnly cookie that
    // JavaScript cannot read, so any client-side "am I logged in?" guess would
    // wrongly show returning users as anonymous after a reload (DT-P2-09).
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    scheduleTokenRefresh();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    scheduleTokenRefresh();
    return data.user;
  };

  const requestMobileOtp = async (phone) => {
    const { data } = await api.post("/auth/mobile/request-otp", { phone });
    return data;
  };

  const verifyMobileOtp = async (payload) => {
    const { data } = await api.post("/auth/mobile/verify-otp", payload);
    setUser(data.user);
    scheduleTokenRefresh();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side revocation failed (offline, 5xx). We still clear local
      // state so the UI doesn't strand the user in a half-signed-in view, but
      // log it: the session cookie may still be live until it expires.
      console.error("Logout request failed; clearing client state anyway:", e);
    }
    clearRefreshTimer();
    setUser(false);
  };

  // Memoised so every consumer of this context doesn't re-render whenever the
  // provider happens to re-render. The auth functions are stable for the life
  // of the provider, so only `user` and `loading` drive a new value.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, requestMobileOtp, verifyMobileOtp }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the handlers are
    // defined once per provider instance and never reassigned.
    [user, loading, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
